import '../../Library3D';

import { blobURLToBase64, generateImageFromText } from '../../helpers/imageHelpers';
import {
    createFabricCanvas,
    createFabricImageObjectFromSrc,
    createFabricTextObject,
    deleteFabricCanvas,
} from '../../helpers/fabricJsHelpers';

import Areas from './SubmenuItems/Areas';
import ColorPickerPopup from './Popups/ColorPickerPopup';
import Colors from './SubmenuItems/Colors';
import ConfiguratorHead from './ConfiguratorHead';
import ConfirmationPopup from './Popups/ConfirmationPopup';
import CustomizationTypeSelect from './SubmenuItems/CustomizationTypeSelect';
import DyeableParts from './SubmenuItems/DyeableParts';
import IconWithLabel from '../IconWithLabel';
import Images from './SubmenuItems/Images';
import Isvg from 'react-inlinesvg';
import Loader from '../Loader';
import LocaleText from '../LocaleText';
import Materials from './SubmenuItems/Materials';
import MenuItem from '../MenuItem';
import Mine from './SubmenuItems/Mine';
import NoticePopup from './Popups/NoticePopup';
import PrintableInfoPopup from './Popups/PrintableInfoPopup';
import PropTypes from 'prop-types';
import React from 'react';
import Scene from './Scene';
import Submenu from './Submenu';
import Templates from './SubmenuItems/Templates';
import TextEditorPopup from './Popups/TextEditorPopup';
import Texts from './SubmenuItems/Texts';
import TransformCanvasContainer from '../../containers/TransformCanvasContainer';
import _ from 'lodash';
import { clearCanvasContext } from '../../helpers/canvasHelpers';
import { formatAmount } from '../../helpers/formatingHelpers';
import history from '../../history';
import settings from '../../settings';

/*
global b4w
*/

class Configurator extends React.Component {
    constructor() {
        super();
        this.lib3d = b4w.require('Library3D');
        this.onPartSelect = this.onPartSelect.bind(this);
        this.onMaterialSelect = this.onMaterialSelect.bind(this);
        this.onColorSelect = this.onColorSelect.bind(this);
        this.onTemplateSelect = this.onTemplateSelect.bind(this);
        this.onMineSelect = this.onMineSelect.bind(this);
        this.onMineColorSelect = this.onMineColorSelect.bind(this);
        this.onAreaSelect = this.onAreaSelect.bind(this);
        this.onImageSelect = this.onImageSelect.bind(this);
        this.onImageUpload = this.onImageUpload.bind(this);
        this.onTextSelect = this.onTextSelect.bind(this);
        this.onCheckout = this.onCheckout.bind(this);
        this.applyTemplateToConfig = this.applyTemplateToConfig.bind(this);
        this.colorAndMaterialSubmenuProps = this.colorAndMaterialSubmenuProps.bind(this);
        this.setCustomizationType = this.setCustomizationType.bind(this);
        this.unsetActiveMenuItem = this.unsetActiveMenuItem.bind(this);
        this.resetConfiguration = this.resetConfiguration.bind(this);
        this.addCustomColor = this.addCustomColor.bind(this);
        this.addCustomText = this.addCustomText.bind(this);
        this.renderColorAndMaterialMenuItem = this.renderColorAndMaterialMenuItem.bind(this);
        this.renderPrintableAreasMenuItem = this.renderPrintableAreasMenuItem.bind(this);
        this.renderEngravingAreasMenuItem = this.renderEngravingAreasMenuItem.bind(this);
        this.renderMineColorMenuItem = this.renderMineColorMenuItem.bind(this);
        this.renderMenu = this.renderMenu.bind(this);
        this.resetPrintableAreas = this.resetPrintableAreas.bind(this);
        this.resetCanvasContextOfArea = this.resetCanvasContextOfArea.bind(this);
        this.applyConfigurationToAreas = this.applyConfigurationToAreas.bind(this);
        this.removeActiveAreaFromConfig = this.removeActiveAreaFromConfig.bind(this);
        this.onMenuItemSelect = this.onMenuItemSelect.bind(this);
        this.renderSubmenuNotification = this.renderSubmenuNotification.bind(this);
        this.setPrintableAreaBackgroundColors = this.setPrintableAreaBackgroundColors.bind(this);
        this.prepareTransformArea = this.prepareTransformArea.bind(this);
        this.getDisabledAreas = this.getDisabledAreas.bind(this);
        this.showPrintableInfoPopup = this.showPrintableInfoPopup.bind(this);
    }

    componentDidMount() {
        const {
            locale,
            fetchPenData,
            initScene,
            setIsLoading,
            minimumOrderMiddleware,
        } = this.props;
        const slug = this.props.match.params.pen;
        initScene(slug)
            .then(() => fetchPenData(slug, locale))
            .then(config => {
                minimumOrderMiddleware(slug, config);
                this.applyConfig(config);
                this.applyConfigurationToAreas(config);
                setIsLoading(false);
            });
    }

    componentWillUnmount() {
        b4w.require('main').reset();
        this.props.setIsLoading(true);
        this.props.resetTransformArea();
        this.props.setCustomizationType(null);
        this.props.setActiveArea(null);
        this.props.setActiveMenuItem(null);
        this.props.unsetActives();
    }

    componentWillUpdate(nextProps) {
        const { isLoading, minimumOrderMiddleware, config } = this.props;

        if (isLoading) {
            return;
        }

        // omit transformations of printable and engraving areas in config comparison
        const printableAreaPaths = Object.keys(config.printableAreas).map(
            area => `printableAreas.${area}.transformation`,
        );
        const engravingAreaPaths = Object.keys(config.engravingAreas).map(
            area => `engravingAreas.${area}.transformation`,
        );

        const paths = [...printableAreaPaths, ...engravingAreaPaths];

        if (!_.isEqual(_.omit(config, paths), _.omit(nextProps.config, paths))) {
            minimumOrderMiddleware(this.props.match.params.pen, nextProps.config);
            this.lib3d.setConfiguration(nextProps.config);

            if (this.lib3d.isMineTipColorful()) {
                const partName = this.lib3d.getMineTipPartName();
                this.lib3d.setColor(partName, nextProps.activeMineColor);
            }
        }
    }

    getDisabledAreas() {
        const { printableAreas, engravingAreas, config } = this.props;
        let disabledAreas = [];

        Object.keys(config.printableAreas).forEach(key => {
            disabledAreas = disabledAreas.concat(printableAreas[key].disabled_areas);
        });

        Object.keys(config.engravingAreas).forEach(key => {
            disabledAreas = disabledAreas.concat(engravingAreas[key].disabled_areas);
        });

        return disabledAreas;
    }

    applyConfigurationToAreas(config) {
        const { customImages, customTexts, printableAreas, engravingAreas, penName } = this.props;
        const areas = { ...config.printableAreas, ...config.engravingAreas };
        // loop over all areas
        const promises = Object.keys(areas).map((areaName, index) => {
            const areaConfig = areas[areaName];
            const context = this.lib3d.getCanvasContext(areaName);
            const { width, height, x, y, rotation } = areaConfig.transformation;
            let area, areaType;

            if (printableAreas.hasOwnProperty(areaName)) {
                area = printableAreas[areaName];
                areaType = 'printable-area';
            }

            if (engravingAreas.hasOwnProperty(areaName)) {
                area = engravingAreas[areaName];
                areaType = 'engraving-area';
            }

            if (areaConfig.type === 'image') {
                const image = customImages.find(o => o.id === areaConfig.image);
                return new Promise((resolve, reject) => {
                    const id = `${areaName}-${areaConfig.type}-${areaConfig.image}-${index}`; // unique ID
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.id = id;
                    document.body.appendChild(tempCanvas);
                    const fabricCanvas = createFabricCanvas(id, area.width, area.height);
                    const src = areaType === 'engraving-area' ? image.filteredSrc : image.src;

                    createFabricImageObjectFromSrc(src, {
                        left: x / window.devicePixelRatio,
                        top: y / window.devicePixelRatio,
                        angle: rotation,
                        originX: 'center',
                        originY: 'center',
                    }).then(imageObject => {
                        imageObject.scaleToWidth(width / window.devicePixelRatio);
                        imageObject.scaleToHeight(height / window.devicePixelRatio);
                        fabricCanvas.add(imageObject);
                        fabricCanvas.renderAll();

                        context.drawImage(tempCanvas, 0, (1024 - area.height) / 2);
                        this.lib3d.setCanvasContext(areaName, context);

                        deleteFabricCanvas(fabricCanvas);
                        document.body.removeChild(tempCanvas);

                        resolve();
                    });
                });
            }

            if (areaConfig.type === 'text') {
                const text = customTexts.find(o => o.id === areaConfig.text);
                return new Promise((resolve, reject) => {
                    const id = `${_.snakeCase(penName)}-${areaName}-${areaConfig.type}`;
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.id = id;
                    document.body.appendChild(tempCanvas);
                    const fabricCanvas = createFabricCanvas(id, area.width, area.height);
                    const fill =
                        areaType === 'engraving-area'
                            ? settings.colors.brown
                            : `rgb(${text.color.r}, ${text.color.g}, ${text.color.b})`;

                    createFabricTextObject(text.text, {
                        left: x / window.devicePixelRatio,
                        top: y / window.devicePixelRatio,
                        angle: rotation,
                        originX: 'center',
                        originY: 'center',
                        fontFamily: text.font,
                        fontWeight: text.bold ? 'bold' : 'normal',
                        fontStyle: text.italic ? 'italic' : 'normal',
                        textAlign: text.alignment || 'left',
                        fill,
                    }).then(textObject => {
                        textObject.scaleToWidth(width / window.devicePixelRatio);
                        textObject.scaleToHeight(height / window.devicePixelRatio);
                        fabricCanvas.add(textObject);
                        fabricCanvas.renderAll();

                        context.drawImage(tempCanvas, 0, (1024 - area.height) / 2);
                        this.lib3d.setCanvasContext(areaName, context);

                        deleteFabricCanvas(fabricCanvas);
                        document.body.removeChild(tempCanvas);

                        resolve();
                    });
                });
            }
        });

        return Promise.all(promises);
    }

    /**
     * Applies config to 3d model, returns a promise
     * @param config
     * @returns {Promise}
     */
    applyConfig(config) {
        return new Promise((resolve, reject) => {
            this.lib3d.setConfiguration(config);
            resolve(config);
        });
    }

    /**
     * Check if config is equal with one of the given templates
     * @param config
     * @param templates
     * @returns {*}
     */
    configEqualsTemplate(config, templates) {
        let equalsTemplate = null;

        Object.keys(templates).forEach(key => {
            if (_.isEqual(templates[key].parts, config.parts)) {
                equalsTemplate = templates[key].id;
            }
        });

        if (equalsTemplate !== null && templates[equalsTemplate].mine_color === config.mine_color) {
            return equalsTemplate;
        }

        return null;
    }

    /**
     * Sets activePart to given part, sets activeMaterial from config,
     * also focuses scene camera to given Part
     * @param activePart
     */
    onPartSelect(activePart) {
        this.props.setActivePart(activePart);
        this.props.setActiveMaterial(this.props.config.parts[activePart].material);
    }

    /**
     * Sets activeMaterial to given material
     * @param activeMaterial
     */
    onMaterialSelect(activeMaterial) {
        this.props.setActiveMaterial(activeMaterial);
    }

    /**
     * Update config (part, material, color, template)
     * @param activeColor
     */
    onColorSelect(activeColor) {
        const {
            activePart,
            activeMaterial,
            setPartMaterialAndColor,
            setTemplate,
            config,
            templates,
        } = this.props;

        const template = this.configEqualsTemplate(config, templates);

        setTemplate(template);
        setPartMaterialAndColor(activePart, activeMaterial, activeColor);
        // check if the current color matches a predefined pantone color in the templates respecting the actual activeMaterial
        const findMatchingPantoneColorWithMaterial = Object.values(templates).filter(template => {
            const part = _.get(template, `parts[${activePart}]`);
            const hasMaterialMatch = part.material === activeMaterial;
            const materialColor = part.color;
            const hasMaterialColorMatch =
                materialColor.r === activeColor.r &&
                materialColor.g === activeColor.g &&
                materialColor.b === activeColor.b;

            return hasMaterialMatch === true && hasMaterialColorMatch === true;
        });
        const hasMatchingPantone = findMatchingPantoneColorWithMaterial.length > 0;
        const activeColorWithPotentialPantoneMatch = hasMatchingPantone
            ? findMatchingPantoneColorWithMaterial[0].parts[activePart].color
            : activeColor;

        setPartMaterialAndColor(activePart, activeMaterial, activeColorWithPotentialPantoneMatch);
    }

    /**
     * Sets activeTemplate, applies template to 3d model and set template configuration to config
     * @param activeTemplate
     */
    onTemplateSelect(activeTemplate) {
        this.props.setTemplate(activeTemplate);
        this.props.setActiveMineColor(this.props.templates[activeTemplate].mine_color);
        this.lib3d.setConfiguration(this.props.templates[activeTemplate]);
        this.applyTemplateToConfig(activeTemplate);
    }

    /**
     * Sets activeMine, set activeMineColor to null
     * @param activeMine
     */
    onMineSelect(activeMine) {
        this.props.setActiveMine(activeMine);
        this.props.setActiveMineColor(null);
    }

    /**
     * Sets image and container for TransformArea
     * @param areaName
     */
    onAreaSelect(areaName) {
        const {
            config,
            customImages,
            customTexts,
            setActiveImage,
            setActiveText,
            setActiveArea,
            setContainer,
            setImage,
            setText,
            resetTransformArea,
            setCustomizationType,
            openTransformArea,
            printableAreas,
            engravingAreas,
            penSlug,
            areaSelectMiddleware,
        } = this.props;

        const configuredAreas = { ...config.printableAreas, ...config.engravingAreas };

        resetTransformArea();

        setActiveArea(areaName);
        this.lib3d.focusCameraOn(areaName);

        areaSelectMiddleware(penSlug, areaName);

        // return if there is no configuration for this area
        if (!configuredAreas.hasOwnProperty(areaName)) {
            setCustomizationType(null);
            setActiveImage(null);
            setActiveText(null);
            return;
        }

        let areaConfig, containerType, area;

        if (config.printableAreas.hasOwnProperty(areaName)) {
            area = printableAreas[areaName];
            areaConfig = config.printableAreas[areaName];
            containerType = 'printable-area';
        }

        if (config.engravingAreas.hasOwnProperty(areaName)) {
            area = engravingAreas[areaName];
            areaConfig = config.engravingAreas[areaName];
            containerType = 'engraving-area';
        }

        const { type, image, text } = areaConfig;
        let obj;
        switch (type) {
            case 'image':
                obj = customImages.find(o => o.id === image);
                setCustomizationType('image');
                setActiveText(null);
                setActiveImage(obj.id);
                setImage(obj);
                break;
            case 'text':
                obj = customTexts.find(o => o.id === text);
                setCustomizationType('text');
                setActiveImage(null);
                setActiveText(obj.id);
                setText(obj);
                break;
            default:
                setCustomizationType(null);
        }

        if (!obj) {
            console.error('Missing imgObj');
            return;
        }

        setContainer(area, containerType);
        openTransformArea();
    }

    removeActiveAreaFromConfig() {
        const { printableAreas, engravingAreas, config, activeArea, setConfig } = this.props;
        const newConfig = _.cloneDeep(config);
        // remove entry of activeArea from config if it exists
        if (printableAreas.hasOwnProperty(activeArea)) {
            delete newConfig.printableAreas[activeArea];
            setConfig(newConfig);
        }

        if (engravingAreas.hasOwnProperty(activeArea)) {
            delete newConfig.engravingAreas[activeArea];
            setConfig(newConfig);
        }
    }

    /**
     *
     * @param imageId
     */
    onImageSelect(imageId) {
        const { customImages } = this.props;
        const i = customImages.findIndex(o => o.id === imageId);
        this.props.closeTransformArea();
        this.props.setActiveImage(imageId);
        this.props.setActiveText(null);
        this.prepareTransformArea();
        this.props.setImage(customImages[i]);
        this.props.setText(null);
        this.props.openTransformArea();
    }

    onImageUpload(e) {
        this.props.onImageUpload(e).then(image => this.onImageSelect(image.id));
    }

    prepareTransformArea() {
        const { printableAreas, engravingAreas, activeArea } = this.props;
        let area, containerType;

        if (printableAreas.hasOwnProperty(activeArea)) {
            area = printableAreas[activeArea];
            containerType = 'printable-area';
        }

        if (engravingAreas.hasOwnProperty(activeArea)) {
            area = engravingAreas[activeArea];
            containerType = 'engraving-area';
        }

        this.removeActiveAreaFromConfig();
        this.resetCanvasContextOfArea(activeArea);
        this.props.setContainer(area, containerType);
    }

    onTextSelect(text) {
        this.props.closeTransformArea();
        this.props.setActiveImage(null);
        this.props.setActiveText(text.id);
        this.props.setText(text);
        this.props.setImage(null);
        this.prepareTransformArea();
        this.props.openTransformArea();
    }

    /**
     * Sets activeMineColor and sets activeMineColor and activeMine to config
     * @param activeMineColor
     */
    onMineColorSelect(activeMineColor) {
        const config = _.cloneDeep(this.props.config);
        config.mine.id = this.props.activeMine;
        const template = this.configEqualsTemplate(config, this.props.templates);

        this.props.setTemplate(template);
        this.props.setMineColor(activeMineColor);
        this.props.setActiveMineColor(activeMineColor);
    }

    onMenuItemSelect(key) {
        this.props.resetTransformArea();
        this.props.unsetActives();
        this.props.setActiveArea(null);
        this.props.setActiveText(null);
        this.props.setActiveImage(null);
        this.props.setCustomizationType(null);
        this.props.setActiveMenuItem(key);
    }

    /**
     * Sets customizationType to state
     * @param customizationType
     */
    setCustomizationType(customizationType) {
        if (
            this.props.config.template === null &&
            customizationType === 'template' &&
            !this.props.isTemplateWarningPopupDisabled
        ) {
            this.props.setIsTemplateWarningPopupVisible(true);
            return;
        }

        this.props.setCustomizationType(customizationType);
        this.props.unsetActives();
        this.props.setIsTemplateWarningPopupVisible(false);
        this.lib3d.setConfiguration(this.props.config);
    }

    /**
     * Show PrintableInfo to state
     */
    showPrintableInfoPopup() {
        if (
            !this.props.isPrintableInfoPopupDisabled
        ) {
            this.props.setIsPrintableInfoPopupVisible(true);
        }
    }


    /**
     * Renders mine colors  (Color Component), returns jsx if mine is set, null otherwise
     * @returns {*}
     */
    renderMineColors() {
        if (!this.props.mine) {
            return null;
        }

        const { mine, activeMineColor } = this.props;

        return (
            <Colors
                colors={[...mine.colors]}
                activeColor={activeMineColor}
                setActiveColor={this.onMineColorSelect}
            />
        );
    }

    /**
     * Returns Materials component if activePart is an actual part,
     * returns null otherwise
     * @returns {XML} or null
     */
    renderMaterials() {
        const { activePart } = this.props;

        if (!this.props.parts.hasOwnProperty(activePart)) {
            return null;
        }

        if (this.props.customizationType !== 'custom') {
            return null;
        }

        const partConfig = this.props.config.parts[activePart];
        const activeMaterial = this.props.activeMaterial || partConfig.material;
        return (
            <Materials
                materials={this.props.parts[activePart].materials}
                activeMaterial={activeMaterial}
                setActiveMaterial={this.onMaterialSelect}
            />
        );
    }

    /**
     * Returns Templates if isTemplateSelectionOpen is true
     * @returns {XML}
     */
    renderTemplates() {
        if (this.props.customizationType !== 'template') {
            return null;
        }

        if (this.props.isLoading) {
            return null;
        }

        return (
            <Templates
                activeTemplate={this.props.activeTemplate}
                setActiveTemplate={this.onTemplateSelect}
                templates={this.props.templates}
            />
        );
    }

    /**
     * Renders DyeableParts component
     * @returns {XML}
     */
    renderDyeableParts() {
        if (this.props.customizationType !== 'custom') {
            return null;
        }
        return (
            <DyeableParts
                parts={this.props.parts}
                activePart={this.props.activePart}
                setActivePart={this.onPartSelect}
            />
        );
    }

    /**
     * Applies activeTemplates parts to config,
     * sets activeTemplate to previewTemplate and previewTemplate to null
     */
    applyTemplateToConfig(templateId) {
        const template = _.cloneDeep(this.props.templates[templateId]);
        const config = _.cloneDeep(this.props.config);

        config.parts = template.parts;
        config.template = templateId;
        config.mine_color = template.mine_color;

        this.props.setActiveMineColor(config.mine_color);
        this.props.setIsTemplateWarningPopupVisible(false);
        this.props.setConfig(config);
    }

    /**
     * Builds an props object for color-and-material Submenu,
     * that will be spread onto the Submenu component
     * @returns {*}
     */
    colorAndMaterialSubmenuProps() {
        return {
            label: <LocaleText textKey="configurator.menu.color.and.material" />,
            close: this.unsetActiveMenuItem,
            notification: this.renderSubmenuNotification(),
        };
    }

    /**
     * Builds an props object for printable-area Submenu,
     * that will be spread onto the Submenu component
     * @returns {*}
     */
    printableAreaSubmenuProps() {
        return {
            label: <LocaleText textKey="configurator.menu.printable.area" />,
            close: this.unsetActiveMenuItem,
            icon: <button className="popup-info" onClick={this.showPrintableInfoPopup}>
                <Isvg src="/svg/info-white.svg" />
            </button>,
            aboveHead:
                this.props.isTransformAreaOpen &&
                this.props.activeMenuItem === 'printable-areas' ? (
                    <div>
                        <TransformCanvasContainer id="transform-canvas-printable-area" />
                    </div>
                ) : null,
            notification: this.renderSubmenuNotification(),
        };
    }

    engravingAreaSubmenuProps() {
        return {
            label: <LocaleText textKey="configurator.menu.engravement" />,
            close: this.unsetActiveMenuItem,
            aboveHead:
                this.props.isTransformAreaOpen &&
                this.props.activeMenuItem === 'engraving-areas' ? (
                    <div>
                        <TransformCanvasContainer id="transform-canvas-engraving-area" />
                    </div>
                ) : null,
            notification: this.renderSubmenuNotification(),
        };
    }

    /**
     * Builds an props object for MenuItems, that will be spread onto the MenuItem component
     * @param key
     * @param isDisabled
     * @returns {{key: *, isActive: boolean, isDisabled: *}}
     */
    menuItemProps(key, isDisabled = false) {
        const isActive = isDisabled ? false : this.isActiveMenuItem(key);
        return {
            key,
            isActive,
            isDisabled,
        };
    }

    /**
     * Check if given key is equal to this.props.activeMenuItem
     * @param key
     * @returns {boolean}
     */
    isActiveMenuItem(key) {
        return this.props.activeMenuItem === key;
    }

    /**
     * Reset current configuration by overwriting config with initialConfig,
     * setting all active- states to null and setting scene camera perspective to initial
     */
    resetConfiguration() {
        if (!confirm(this.props.texts['popup.reset.copy'])) {
            return;
        }

        const { initialConfig } = this.props;
        this.props.setConfig(initialConfig);

        // reset configurator
        this.props.setActiveMenuItem(null);
        this.props.unsetActives();
        // this.props.setCustomizationType(null);
        this.props.resetTransformArea();

        // reset 3d model/scene
        this.resetPrintableAreas();
        this.lib3d.setConfiguration(initialConfig);
        this.lib3d.resetCamera();
    }

    /**
     * Loops over all printableAreas that are in config and clears the canvas on the 3d model for each
     */
    resetPrintableAreas() {
        const areas = Object.keys(this.props.config.printableAreas);
        areas.map(area => this.resetCanvasContextOfArea(area));
    }

    /**
     * Clears the canvas of the given area
     * @param area
     */
    resetCanvasContextOfArea(area) {
        const clearedContext = clearCanvasContext(this.lib3d.getCanvasContext(area));
        this.lib3d.setCanvasContext(area, clearedContext);
    }

    /**
     * Sets activeMenuItem to null, if an event is given event propagation is stopped
     * @param event
     */
    unsetActiveMenuItem(event = null) {
        this.props.setActiveMenuItem(null);
        if (event) {
            event.stopPropagation(); // prevent event bubbling to menu item
        }
    }

    /**
     * Initialize 3d scene and load 3d data
     * @param penName
     * @returns {Promise}
     */
    initScene(penName) {
        return new Promise(resolve => {
            this.lib3d.init(penName, resolve, () => {
            });
        });
    }

    /**
     * Add color to customColors array in state
     * @param color
     */
    addCustomColor() {
        // get rid of alpha value
        const rgbOnly = _.omit(this.props.customColor, 'a');

        this.props.closeColorPicker();

        switch (this.props.customizationType) {
            case 'custom':
                this.onColorSelect(rgbOnly);
                this.props.addCustomColor(rgbOnly);
                break;
            case 'text':
                this.props.setTextColor(rgbOnly);
                break;
        }
    }

    /**
     *
     * @returns {{image: {src: string, width: number, height: number, id: *}, id: number}}
     */
    addCustomText() {
        this.props.closeTextEditor();
        const id = Date.now();

        // create customText object from textEditor text object (which only includes information of the text, like style)
        generateImageFromText(this.props.customText, id).then(image => {
            const text = {
                image,
                id,
                ...this.props.customText,
            };

            this.props.addCustomText(text);
            this.props.resetTextEditor();
            this.onTextSelect(text);
        });
    }

    renderSubmenuNotification() {
        const classes = ['submenu__notification'];
        if (this.props.isNotificationVisible) {
            classes.push('is-visible');
        }
        return (
            <div className={classes.join(' ')}>
                <Isvg src="/svg/warning-sign.svg" />
                <LocaleText
                    textKey="checkout.min.order.amount"
                    args={[formatAmount(this.props.config.minOrderAmount)]}
                />
            </div>
        );
    }

    /**
     * Returns Colors component if activePart is an actual part,
     * returns null otherwise
     * @returns {XML} or null
     */
    renderColors() {
        const { activePart } = this.props;

        if (!this.props.parts.hasOwnProperty(activePart)) {
            return null;
        }

        if (this.props.customizationType !== 'custom') {
            return null;
        }

        const partConfig = this.props.config.parts[activePart];
        const activeMaterial =
            this.props.activeMaterial || this.props.config.parts[activePart].material;
        const activeColor = partConfig.color;
        const { colors } = this.props.parts[activePart].materials[activeMaterial];
        const allowsCustomColors = this.props.parts[activePart].materials[activeMaterial]
            .custom_colors;
        return (
            <Colors
                colors={colors}
                customColors={this.props.customColors}
                allowCustomColors={allowsCustomColors}
                activeColor={activeColor}
                setActiveColor={this.onColorSelect}
                openColorPickerPopup={this.props.openColorPicker}
            />
        );
    }

    renderColorAndMaterialMenuItem() {
        const { penSlug } = this.props;
        const types = [
            {
                key: 'template',
                value: (
                    <LocaleText textKey="configurator.customization.type.templates" />
                ),
            },
        ];

        // only show custom option for pens that are NOT in the pensWithoutCustomCustomizationOption array of settings.js
        if (!settings.configurator.pensWithoutCustomCustomizationOption.includes(penSlug)) {
            types.push({
                key: 'custom',
                value: (
                    <LocaleText textKey="configurator.customization.type.custom" />
                ),
            });
        }

        return (
            <MenuItem {...this.menuItemProps('color-and-material')}>
                <IconWithLabel
                    icon={{
                        src: '/svg/color.svg',
                        type: 'svg',
                    }}
                    iconActive={{
                        src: '/svg/color-filled.svg',
                        type: 'svg',
                    }}
                    label={<LocaleText textKey="configurator.menu.color.and.material" />}
                    onClick={() => this.onMenuItemSelect('color-and-material')}
                />
                <Submenu {...this.colorAndMaterialSubmenuProps()}>
                    <CustomizationTypeSelect
                        types={types}
                        activeType={this.props.customizationType}
                        onChange={this.setCustomizationType}
                    />
                    {this.renderTemplates()}
                    {this.renderDyeableParts()}
                    {this.renderMaterials()}
                    {this.renderColors()}
                </Submenu>
            </MenuItem>
        );
    }

    renderPrintableAreasMenuItem() {
        const { printableAreas, activeArea, shouldPrintableInfoPopupBeDisabled } = this.props;
        const printableAreaKeys = Object.keys(printableAreas);

        // check if printableAreas included areas that start with DIG,
        // as those are digital print areas
        let hasDigitalPrintAreas = false;
        if (printableAreaKeys.find(key => key.substr(0, 1) === 'D')) {
            hasDigitalPrintAreas = true;
        }

        return (
            <MenuItem {...this.menuItemProps('printable-areas')}>
                <IconWithLabel
                    icon={{
                        src: '/svg/print.svg',
                        type: 'svg',
                    }}
                    iconActive={{
                        src: '/svg/print-filled.svg',
                        type: 'svg',
                    }}
                    label={<LocaleText textKey="configurator.menu.printable.area" />}
                    onClick={() => {
                        this.onMenuItemSelect('printable-areas');
                        if (!shouldPrintableInfoPopupBeDisabled && hasDigitalPrintAreas) {
                            this.showPrintableInfoPopup();
                        }
                    }}
                />
                <Submenu {...this.printableAreaSubmenuProps()}>
                    {this.props.activeMenuItem === 'printable-areas' ? (
                        <Areas
                            areas={printableAreas}
                            disabledAreas={this.getDisabledAreas()}
                            activeArea={activeArea}
                            onAreaSelect={this.onAreaSelect}
                        />
                    ) : null}
                    {this.props.activeArea ? (
                        <CustomizationTypeSelect
                            types={[
                                {
                                    key: 'image',
                                    value: (
                                        <LocaleText textKey="configurator.customization.type.logo" />
                                    ),
                                },
                                {
                                    key: 'text',
                                    value: (
                                        <LocaleText textKey="configurator.customization.type.text" />
                                    ),
                                },
                            ]}
                            activeType={this.props.customizationType}
                            onChange={this.setCustomizationType}
                        />
                    ) : null}
                    {this.props.customizationType === 'image' ? (
                        <Images
                            activeImage={this.props.activeImage}
                            onImageSelect={this.onImageSelect}
                            onImageUpload={this.onImageUpload}
                            onImageRemove={this.props.removeCustomImage}
                            images={this.props.customImages}
                        />
                    ) : null}
                    {this.props.customizationType === 'text' ? (
                        <Texts
                            onAddNewText={this.props.openTextEditor}
                            onTextRemove={this.props.removeCustomText}
                            activeText={this.props.activeText}
                            onTextSelect={this.onTextSelect}
                            texts={this.props.customTexts}
                        />
                    ) : null}
                </Submenu>
            </MenuItem>
        );
    }

    renderEngravingAreasMenuItem() {
        const isDisabled = _.isEmpty(this.props.engravingAreas);
        const iconClickHandler = isDisabled
            ? undefined
            : () => this.onMenuItemSelect('engraving-areas');
        return (
            <MenuItem {...this.menuItemProps('engraving-areas', isDisabled)}>
                <IconWithLabel
                    icon={{
                        src: '/svg/engravement.svg',
                        type: 'svg',
                    }}
                    iconActive={{
                        src: '/svg/engravement-filled.svg',
                        type: 'svg',
                    }}
                    label={<LocaleText textKey="configurator.menu.engravement" />}
                    onClick={iconClickHandler}
                />
                <Submenu {...this.engravingAreaSubmenuProps()}>
                    {this.props.activeMenuItem === 'engraving-areas' ? (
                        <Areas
                            disabledAreas={this.getDisabledAreas()}
                            areas={this.props.engravingAreas}
                            activeArea={this.props.activeArea}
                            onAreaSelect={this.onAreaSelect}
                        />
                    ) : null}
                    {this.props.activeArea ? (
                        <CustomizationTypeSelect
                            types={[
                                {
                                    key: 'image',
                                    value: (
                                        <LocaleText textKey="configurator.customization.type.logo" />
                                    ),
                                },
                                {
                                    key: 'text',
                                    value: (
                                        <LocaleText textKey="configurator.customization.type.text" />
                                    ),
                                },
                            ]}
                            activeType={this.props.customizationType}
                            onChange={this.setCustomizationType}
                        />
                    ) : null}
                    {this.props.customizationType === 'image' ? (
                        <Images
                            activeImage={this.props.activeImage}
                            onImageSelect={this.onImageSelect}
                            onImageUpload={this.props.onImageUpload}
                            onImageRemove={this.props.removeCustomImage}
                            images={this.props.customImages}
                        />
                    ) : null}
                    {this.props.customizationType === 'text' ? (
                        <Texts
                            onAddNewText={this.props.openTextEditor}
                            onTextRemove={this.props.removeCustomText}
                            activeText={this.props.activeText}
                            onTextSelect={this.onTextSelect}
                            texts={this.props.customTexts}
                        />
                    ) : null}
                </Submenu>
            </MenuItem>
        );
    }

    renderMineColorMenuItem() {
        const isDisabled = _.isEmpty(this.props.mine);
        const iconClickHandler = isDisabled ? undefined : () => this.onMenuItemSelect('mine');
        const labelKey = this.props.mine.id
            ? 'configurator.menu.mine'
            : 'configurator.menu.writing.color';
        return (
            <MenuItem {...this.menuItemProps('mine', isDisabled)}>
                <IconWithLabel
                    extraClasses={['has-writing-svg']}
                    icon={{
                        src: '/svg/writing-color.svg',
                        type: 'svg',
                    }}
                    iconActive={{
                        src: '/svg/writing-color-filled.svg',
                        type: 'svg',
                    }}
                    label={<LocaleText textKey={labelKey} />}
                    onClick={iconClickHandler}
                />
                {isDisabled ? null : (
                    <Submenu
                        label={<LocaleText textKey={labelKey} />}
                        close={this.unsetActiveMenuItem}
                        notification={this.renderSubmenuNotification()}
                    >
                        {this.props.mine.id && ( // only show mine menu if pen has an actual mine, not just different writing colors
                            <Mine
                                setActiveMine={this.onMineSelect}
                                activeMine={this.props.activeMine}
                                mine={this.props.mine}
                            />
                        )}
                        {this.props.mine.colors && this.renderMineColors()}
                    </Submenu>
                )}
            </MenuItem>
        );
    }

    /**
     * Hides scene, sets isLoading to true, calls getPenShots method of lib3d, which
     * returns blobURLs which then are converted to base64 and those are finally saved to
     * state and user gets directed to checkout page
     */
    onCheckout() {
        const { lang } = this.props.match.params;
        const {
            setIsLoading,
            setCustomizationType,
            setActiveMenuItem,
            setPenShots,
            config,
            setCheckoutBehaviour,
            setCheckoutStep,
        } = this.props;

        // make sure to set correct configuration to pen
        this.applyConfig(config).then(() => {
            // immediately hide scene, because it gets ugly when screenshots are taken
            const scene = document.querySelector('#scene3d');
            scene.classList.add('visibility-hidden');
            // set is loading to true
            setIsLoading(true);
            setCustomizationType(null);
            setActiveMenuItem(null);

            // add background_color value to each printableArea
            this.setPrintableAreaBackgroundColors();

            // take penShots
            this.lib3d.getPenShots().then(blobURLs => {
                const promises = blobURLs.map(blobURL => blobURLToBase64(blobURL));
                Promise.all(promises).then(shots => {
                    // save them to state
                    setPenShots(shots);

                    // prepare checkout
                    setCheckoutBehaviour('DEFAULT');
                    setCheckoutStep(0);

                    // redirect to checkout
                    history.push(`/${lang}/checkout`);
                });
            });
        });
    }

    setPrintableAreaBackgroundColors() {
        const { config, setConfig } = this.props;
        const newConfig = _.cloneDeep(config);

        // return early if there are no configured printable areas
        if (_.isEmpty(config.printableAreas)) {
            return;
        }

        // loop over all configured printable areas and set its background_color
        Object.keys(newConfig.printableAreas).forEach(key => {
            newConfig.printableAreas[key].background_color = this.lib3d.getParentColor(key);
        });

        setConfig(newConfig);
    }

    renderMenu() {
        if (this.props.isLoading) {
            return null;
        }

        return (
            <aside className="configurator__menu">
                <nav className="nav">
                    <ul className="menu">
                        {this.renderColorAndMaterialMenuItem()}
                        {this.renderPrintableAreasMenuItem()}
                        {this.renderEngravingAreasMenuItem()}
                        {this.renderMineColorMenuItem()}
                        <MenuItem onClick={this.onCheckout}>
                            <IconWithLabel
                                icon={{
                                    src: '/svg/checkout.svg',
                                    type: 'svg',
                                }}
                                label={<LocaleText textKey="configurator.menu.request" />}
                            />
                        </MenuItem>
                    </ul>
                </nav>
            </aside>
        );
    }

    render() {
        const classes = ['configurator'];
        if (this.props.isLoading) {
            classes.push('is-loading');
        }
        let mineColor, writingLinesStyle;
        if (!_.isEmpty(this.props.activeMineColor)) {
            mineColor = this.props.activeMineColor;
            writingLinesStyle = `.writing-lines, .has-writing-svg svg .color-drop {fill: rgb(${
                mineColor.r
                }, ${mineColor.g}, ${mineColor.b})!important};`;
        } else {
            writingLinesStyle = `.has-writing-svg svg .color-drop {fill: rgba(255, 255, 255, 0)!important}`;
        }
        return (
            <div className={classes.join(' ')}>
                <Loader />
                <TextEditorPopup
                    onCancel={this.props.closeTextEditor}
                    onConfirm={this.addCustomText}
                    onClose={this.props.closeTextEditor}
                    isVisible={this.props.isTextEditorOpen}
                    showColorSelect={this.props.activeMenuItem !== 'engraving-areas'}
                />
                <ColorPickerPopup
                    onCancel={this.props.closeColorPicker}
                    onConfirm={this.addCustomColor}
                    onClose={this.props.closeColorPicker}
                    isVisible={this.props.isColorPickerOpen}
                />
                <ConfirmationPopup
                    onCancel={() => this.props.setIsTemplateWarningPopupVisible(false)}
                    onConfirm={() => {
                        if (this.props.shouldTemplateWarningPopupBeDisabled) {
                            this.props.disableTemplateWarningPopup();
                        }
                        this.props.setCustomizationType('template');
                        this.props.setIsTemplateWarningPopupVisible(false);
                    }}
                    onCheckboxChange={this.props.toggleShouldTemplateWarningPopupBeDisabled}
                    isVisible={this.props.isTemplateWarningPopupVisible}
                    shouldDisable={this.props.shouldTemplateWarningPopupBeDisabled}
                />
                <NoticePopup
                    onConfirm={this.props.closeNoticePopup}
                    isVisible={this.props.isNoticePopupVisible}
                    {...this.props.noticePopupContent}
                />
                <PrintableInfoPopup
                    onCancel={() => this.props.setIsPrintableInfoPopupVisible(false)}
                    onConfirm={() => {
                        if (this.props.shouldPrintableInfoPopupBeDisabled) {
                            this.props.disablePrintableInfoPopup();
                        }
                        this.props.setIsPrintableInfoPopupVisible(false);
                    }}
                    onCheckboxChange={this.props.toggleShouldPrintableInfoPopupBeDisabled}
                    isVisible={this.props.isPrintableInfoPopupVisible}
                    shouldDisable={this.props.shouldPrintableInfoPopupBeDisabled}
                />
                <ConfiguratorHead
                    penName={this.props.penName}
                    resetConfig={this.resetConfiguration}
                    toggleCap={this.lib3d.toggleCap}
                    hasCap={this.lib3d.hasCap()}
                    toggleIntroductionLayer={this.props.toggleIntroductionLayer}
                    texts={this.props.texts}
                />
                {this.props.activeMenuItem === 'mine' ||
                this.props.customizationType === 'template' ? (
                    <div className="writing-lines">
                        <style type="text/css">{writingLinesStyle}</style>
                        {
                        this.props.penName === 'Take 4 Promo' ?
                        <Isvg src="/svg/writing-line-multi-color.svg" />  :
                        <Isvg src="/svg/writing-line.svg" />
                        }
                    </div>
                ) : null}
                <Scene />
                {this.renderMenu()}
                <style type="text/css">{writingLinesStyle}</style>
            </div>
        );
    }
}

Configurator.propTypes = {
    // data props
    match: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
    penName: PropTypes.string,
    parts: PropTypes.object,
    templates: PropTypes.object,
    mines: PropTypes.array,
    customColors: PropTypes.array,
    config: PropTypes.object,
    initialConfig: PropTypes.object,
    activeMenuItem: PropTypes.string,
    activePart: PropTypes.string,
    activeMaterial: PropTypes.string,
    activeColor: PropTypes.object,
    activeTemplate: PropTypes.number,
    activeMine: PropTypes.number,
    activeMineColor: PropTypes.object,
    isTemplateWarningPopupVisible: PropTypes.bool,
    isTemplateWarningPopupDisabled: PropTypes.bool,
    shouldTemplateWarningPopupBeDisabled: PropTypes.bool,
    isPrintableInfoPopupVisible: PropTypes.bool,
    isPrintableInfoPopupDisabled: PropTypes.bool,
    shouldPrintableInfoPopupBeDisabled: PropTypes.bool,
    isColorPickerOpen: PropTypes.bool,
    customizationType: PropTypes.string,
    isTextEditorOpen: PropTypes.bool,
    isNoticePopupVisible: PropTypes.bool,
    // actions
    setInitialState: PropTypes.func.isRequired,
    setActiveMaterial: PropTypes.func.isRequired,
    setActivePart: PropTypes.func.isRequired,
    setActiveMine: PropTypes.func.isRequired,
    setActiveMineColor: PropTypes.func.isRequired,
    setActiveMenuItem: PropTypes.func.isRequired,
    setConfig: PropTypes.func.isRequired,
    setTemplate: PropTypes.func.isRequired,
    setMineColor: PropTypes.func.isRequired,
    unsetActives: PropTypes.func.isRequired,
    setCustomizationType: PropTypes.func.isRequired,
    setIsTemplateWarningPopupVisible: PropTypes.func.isRequired,
    disableTemplateWarningPopup: PropTypes.func.isRequired,
    setIsPrintableInfoPopupVisible: PropTypes.func.isRequired,
    disablePrintableInfoPopup: PropTypes.func.isRequired,
    openColorPicker: PropTypes.func.isRequired,
    closeColorPicker: PropTypes.func.isRequired,
    addCustomColor: PropTypes.func.isRequired,
    setIsLoading: PropTypes.func.isRequired,
    minimumOrderMiddleware: PropTypes.func.isRequired,
    areaSelectMiddleware: PropTypes.func.isRequired,
    setPartMaterialAndColor: PropTypes.func.isRequired,
    closeNoticePopup: PropTypes.func.isRequired,
    openNoticePopup: PropTypes.func.isRequired,
    toggleShouldTemplateWarningPopupBeDisabled: PropTypes.func.isRequired,
    toggleShouldPrintableInfoPopupBeDisabled: PropTypes.func.isRequired,
};

Configurator.defaultProps = {
    isLoading: true,
    penName: '',
    parts: {},
    templates: {},
    customColors: [],
    config: {},
    mine: null,
    initialConfig: {},
    activeMenuItem: null,
    activePart: null,
    activeMaterial: null,
    activeColor: null,
    activeMine: null,
    activeMineColor: null,
    isTemplateWarningPopupVisible: false,
    isTemplateWarningPopupDisabled: false,
    shouldPrintableInfoPopupBeDisabled: false,
    isPrintableInfoPopupVisible: false,
    isPrintableInfoPopupDisabled: false,
    shouldTemplateWarningPopupBeDisabled: false,
    isColorPickerOpen: false,
    customizationType: null,
    isTextEditorOpen: false,
    isNoticePopupVisible: false,
};

export default Configurator;
